import { CommonModule } from "@angular/common";
import { Component, EventEmitter, inject, Output, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatIconModule } from "@angular/material/icon";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import _ from "lodash";
import { NzIconModule } from "ng-zorro-antd/icon";
import { RestService } from "../../../core/services/rest.service";
import { TdgService } from "../../../core/services/tdg.service";
import { ToasterService } from "../../../core/services/toaster-service";
import { MessageEnum } from "../../../core/utils/message.enum";
import {
  AccordionSection,
  AccordionTableViewComponent,
} from "../../common/accordion-table-view/accordion-table-view.component";
import { ZorroTableComponent } from "../../common/custom-table/zorro-table.component";
import { LoadingComponent } from "../../common/loading/loading.component";
import { SimplePagerComponent } from "../../common/simple-pager/simple-pager.component";
import { AddColumnModalComponent } from "../../modal-templates/add-column-modal/add-column-modal.component";

@Component({
  selector: "app-tdg-version-control",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatExpansionModule,
    ZorroTableComponent,
    LoadingComponent,
    MatIconModule,
    NzIconModule,
    AccordionTableViewComponent,
    SimplePagerComponent,
  ],
  templateUrl: "./tdg-version-control.component.html",
  styleUrl: "./tdg-version-control.component.scss",
})
export class TdgVersionControlComponent {
  @Output() moveToPreviousTabEvent = new EventEmitter<void>();
  @Output() enableTabsEvent = new EventEmitter<number[]>();
  @Output() disableTabsEvent = new EventEmitter<number[]>();
  @Output() testCasePreviewEvent = new EventEmitter<any>();

  @ViewChild(ZorroTableComponent) zorroTableComponent!: ZorroTableComponent;

  selectedSchema: any = "";
  customTables: any = [];
  schemas: any = [];
  isExpanded = true;
  selectedVersion = "";
  isLoading = false;
  expPanelLoading = false;
  automationProjectId = "";
  clonedTable: any;
  isPersisted: boolean = false;
  isDeleting: boolean = false;
  isDropdownOpen: boolean = false;
  private modalService = inject(NgbModal);

  isTSuiteView = true;
  isPersisting = false;

  // Test Suite View properties
  accordionSections: AccordionSection[] = [];
  readonly pageSize = 8;
  currentPage = 0;
  readonly tcPageSize = 6;
  tcCurrentPage = 0;
  pagedTestCaseData: any[] = [];
  readonly tcTableOptions = { showPagination: false, pageSize: 6 };
  selectedTimestamp: string = "";
  isTestCaseDetailView: boolean = false;
  selectedTestCaseDetail: any = null;
  showDownloadMenu: boolean = false;

  testSuiteColumns: any[] = [
    { key: "testCaseId", display_name: "Test Case ID" },
    { key: "testCase", display_name: "Test Case" },
    { key: "testScenario", display_name: "Test Scenario" },
    { key: "updated_at", display_name: "Timestamp" },
    {
      key: "action",
      display_name: "",
      _config: {
        type: "button",
        name: "Preview",
        classNames: "btn-accello-secondary btn-accello w-max"
      }
    }
  ];

  testCaseColumns: any[] = [];

  testSuiteData: any[] = [
    { testSuite: "test_suites_1", testCaseId: "TC001", testCaseTitle: "Login Test", timestamp: "2025-03-18_07:24:29" },
    { testCaseId: "TC002", testCaseTitle: "Logout Test", timestamp: "2025-03-18_07:24:30" },
    { testCaseId: "TC003", testCaseTitle: "Dashboard Test", timestamp: "2025-03-18_07:24:31" },
    { testCaseId: "TC004", testCaseTitle: "Profile Update Test", timestamp: "2025-03-18_07:24:32" },
    { testCaseId: "TC005", testCaseTitle: "Search Test", timestamp: "2025-03-18_07:24:33" },
    { testCaseId: "TC006", testCaseTitle: "Filter Test", timestamp: "2025-03-18_07:24:34" },
    { testCaseId: "TC007", testCaseTitle: "Export Data Test", timestamp: "2025-03-18_07:24:35" },
    { testCaseId: "TC008", testCaseTitle: "Delete Test", timestamp: "2025-03-18_07:24:36" },
  ];

  testCaseData: any[] = [];
  originalTestCaseResponse: any = null;
  showInsertMenu = false;
  isEditMode = false;
  stickyCols: any = {};



  constructor(
    private readonly tdgService: TdgService,
    private readonly restService: RestService,
    private readonly toaster: ToasterService
  ) { }

  ngOnInit() {
    this.disableTabsEvent.emit([1]);
    this.isLoading = true;
    this.loadData();
    this.restService
      .getAutomationProject(this.tdgService.getProjectId())
      .subscribe((response: any) => {
        this.automationProjectId = response.data[0]?.id;
      });
  }

  /** Publicly callable — resets to suite-list view, shows loader, reloads data. */
  refresh(): void {
    this.isTSuiteView = true;
    this.selectedTestCaseDetail = null;
    this.isEditMode = false;
    this.showDownloadMenu = false;
    this.showInsertMenu = false;
    this.tcCurrentPage = 0;
    this.currentPage = 0;
    this.accordionSections = [];
    this.isLoading = true;
    this.loadData();
  }

  loadData() {
    this.expPanelLoading = true;
    this.restService
      .getCustomTableList(this.tdgService.getProjectId())
      .subscribe(
        (response: any) => {
          this.customTables = response?.tableList;
          this.extractTestSuites(response?.tableList);
          this.expPanelLoading = false;
        },
        (error) => {
          console.error(error);
          this.expPanelLoading = false;
          this.isLoading = false;
        }
      );
  }

  extractTestSuites(tableList: any[]): void {
    if (!tableList || tableList.length === 0) {
      this.accordionSections = [];
      this.currentPage = 0;
      this.isLoading = false;
      return;
    }

    const uniqueTestSuites = [...new Set(
      tableList
        .map(item => item.testSuite)
        .filter(testSuite => testSuite != null && testSuite !== '')
    )].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));

    this.accordionSections = uniqueTestSuites.map((suiteName: string) => {
      const count = tableList.filter((item) => item.testSuite === suiteName).length;
      return {
        id: suiteName,
        title: suiteName,
        name: suiteName,
        testSuiteName: suiteName,
        itemCount: count,
        testcaseCount: count,
        expanded: false,
        loading: false,
      } as AccordionSection;
    });
    this.currentPage = 0;
    this.isLoading = false;
  }

  get pagedAccordionSections(): AccordionSection[] {
    const start = this.currentPage * this.pageSize;
    return this.accordionSections.slice(start, start + this.pageSize);
  }

  get pagerStart(): number {
    return this.accordionSections.length === 0 ? 0 : this.currentPage * this.pageSize + 1;
  }

  get pagerEnd(): number {
    return Math.min((this.currentPage + 1) * this.pageSize, this.accordionSections.length);
  }

  get disablePrevPage(): boolean {
    return this.currentPage === 0;
  }

  get disableNextPage(): boolean {
    return (this.currentPage + 1) * this.pageSize >= this.accordionSections.length;
  }

  prevPage(): void {
    if (!this.disablePrevPage) {
      this.currentPage--;
    }
  }

  nextPage(): void {
    if (!this.disableNextPage) {
      this.currentPage++;
    }
  }

  get tcPagerStart(): number {
    return this.testCaseData.length === 0 ? 0 : this.tcCurrentPage * this.tcPageSize + 1;
  }

  get tcPagerEnd(): number {
    return Math.min((this.tcCurrentPage + 1) * this.tcPageSize, this.testCaseData.length);
  }

  get tcDisablePrevPage(): boolean {
    return this.tcCurrentPage === 0;
  }

  get tcDisableNextPage(): boolean {
    return (this.tcCurrentPage + 1) * this.tcPageSize >= this.testCaseData.length;
  }

  private refreshPagedTestCaseData(): void {
    const maxPage = Math.max(0, Math.ceil(this.testCaseData.length / this.tcPageSize) - 1);
    if (this.tcCurrentPage > maxPage) {
      this.tcCurrentPage = maxPage;
    }
    const start = this.tcCurrentPage * this.tcPageSize;
    this.pagedTestCaseData = this.testCaseData.slice(start, start + this.tcPageSize);
  }

  tcPrevPage(): void {
    if (!this.tcDisablePrevPage) {
      this.tcCurrentPage--;
      this.refreshPagedTestCaseData();
    }
  }

  tcNextPage(): void {
    if (!this.tcDisableNextPage) {
      this.tcCurrentPage++;
      this.refreshPagedTestCaseData();
    }
  }

  onSectionExpand(event: { section: AccordionSection; typeId?: string }): void {
    const section = event?.section;
    if (!section || section.tableRows) {
      return;
    }

    section.loading = true;
    section.tableColumns = this.testSuiteColumns;

    this.restService
      .getCustomTablesByTestSuite(this.tdgService.getProjectId(), section.testSuiteName)
      .subscribe({
        next: (response: any) => {
          section.tableRows = response?.data || [];
          section.testcaseCount = response?.count ?? section.tableRows?.length ?? 0;
          section.itemCount = section.testcaseCount;
          section.loading = false;
        },
        error: (error) => {
          console.error('Error loading test cases:', error);
          section.tableRows = [];
          section.loading = false;
        },
      });
  }

  onTableActions(event: { section: AccordionSection; action: any }): void {
    this.onTestCaseAction(event?.action);
  }

  expand() {
    this.isExpanded = !this.isExpanded;
  }

  onVersionSelect(source: any) {
    this.isDropdownOpen = false;
    this.selectedVersion = source.tableId;
    this.loadTableDetails(source.tableId);
    this.isPersisted =
      this.tdgService.getPersistedTable()?.id === this.selectedVersion;
  }

  loadTableDetails(id: string) {
    this.isLoading = true;
    this.restService
      .getCustomTableById(this.tdgService.getProjectId(), id)
      .subscribe(
        (response: any) => {
          this.isLoading = false;
          let tableData = response.tableData;
          tableData.dataSource = response.dataSource;
          this.clonedTable = _.cloneDeep(tableData);
          tableData.columns.forEach((column: any) => {
            column._config = undefined;
          });
          this.schemas = [
            {
              db_name: "Custom_Data",
              tables: [tableData],
            },
          ];
          this.selectedSchema = this.schemas[0];
        },
        (error) => {
          console.error(error);
          this.isLoading = false;
        }
      );
  }

  switchTab(schema: any) {
    this.selectedSchema = _.cloneDeep(schema);
  }

  onEdit() {
    this.showDownloadMenu = false;
    this.isEditMode = true;
    this.testCaseColumns.push({
      key: "edit",
      display_name: "",
      cellStyles: { "font-size": "12px", 'position': 'sticky', 'right': '0', 'z-index': '2', 'background-color': '#fff' },
      headerStyles: {
        background: "#7593F2",
        "font-weight": "600",
        width: "3%",
        color: "#fff",
        position: 'sticky',
        right: '0',
        'z-index': '2',
        'background-color': '#fff'
      },
      _config: {
        type: "svg-editor",
        value: "../../../../assets/images/virtusa-icons/edit-light.svg",
        isClickable: "true",
        actionType: "edit",
      },
      _editConfig: {
        type: "svg-editor",
        actionType: "add",
        value: "../../../../assets/images/virtusa-icons/tick.svg",
        isClickable: "true",
      }
    });

    let conf: any = {
      key: 'rowSelect',
      display_name: '',
      headerStyles: {
        width: '5%'
      },
      _config: {
        type: "checkbox"
      }
    };
    this.testCaseColumns.unshift(conf);
    this.testCaseColumns = [...this.testCaseColumns];
    this.refreshPagedTestCaseData();
    if (this.zorroTableComponent) {
      this.zorroTableComponent.updateOptions({
        ...this.tcTableOptions,
        enableColumnSelection: true,
      });
    }
  }

  assignLevels(data: any[], level: number = 0): any[] {
    return data.map((item) => {
      const hasScript = this.containsScript(item);

      const parts = item?.name?.split("_");
      let name =
        parts?.length > 3
          ? parts.slice(2, -1).join("_")
          : parts?.[2] || item.name;

      const newItem = {
        ...item,
        name,
        level,
        scriptLevel: hasScript,
      };

      if (item.children && item.children.length > 0) {
        newItem.children = this.assignLevels(item.children, level + 1);
      }
      return newItem;
    });
  }

  containsScript(item: any): boolean {
    if (item.script) {
      return true;
    } else {
      return false;
    }
  }


  onTestCaseAction(event: any): void {
    if (event?.type === "button") {
      // Handle Preview button click
      this.selectedTestCaseDetail = event?.data?.row;
      this.isTSuiteView = false;
      this.isLoading = true;
      this.testCasePreviewEvent.emit(true);

      // Call API to get test case details
      this.restService
        .getCustomTableById(this.tdgService.getProjectId(), event?.data?.row?.id)
        .subscribe(
          (response: any) => {
            // Store original response for save operation
            this.originalTestCaseResponse = _.cloneDeep(response);

            // Build columns from API response (mask PII columns like tdg-data-table)
            const piiColumns: string[] = [
              ...(response.tableData?.piiColumns ?? []),
              ...(response.tableData?.pii_columns ?? []),
              ...(response.piiColumns ?? []),
              ...(response.pii_columns ?? []),
            ];
            this.testCaseColumns = response.tableData.columns.map((col: string) => {
              const column: any = {
                key: col,
                display_name: col,
                _editConfig: {
                  type: "input"
                }
              };
              if (col && piiColumns.includes(col)) {
                column._config = {
                  type: 'password',
                  classNames: 'border-0 bg-transparent',
                };
              }
              return column;
            });

            // Add edit column
            // this.testCaseColumns.push({
            //   key: "edit",
            //   display_name: "",
            //   cellStyles: { "font-size": "12px" },
            //   headerStyles: {
            //     background: "#7593F2",
            //     "font-weight": "600",
            //     width: "3%",
            //     color: "#fff",
            //   },
            //   _config: {
            //     type: "svg-editor",
            //     value: "../../../../assets/images/virtusa-icons/edit-light.svg",
            //     isClickable: "true",
            //     actionType: "edit",
            //   },
            //   _editConfig: {
            //     type: "svg-editor",
            //     actionType: "add",
            //     value: "../../../../assets/images/virtusa-icons/tick.svg",
            //     isClickable: "true",
            //   }
            // });

            // Transform rawData array to objects
            this.testCaseData = response.tableData.rawData.map((row: any[], index: number) => {
              let obj: any = { id: index };
              response.tableData.columns.forEach((col: string, colIndex: number) => {
                obj[col] = row[colIndex];
              });
              return obj;
            });
            this.tcCurrentPage = 0;
            this.refreshPagedTestCaseData();

            this.isLoading = false;
          },
          (error) => {
            console.error('Error loading test case details:', error);
            this.isLoading = false;
            this.toaster.pop({
              text: MessageEnum.TEST_CASE_DETAILS_FAILURE,
              type: 'error'
            });
          }
        );
    }

    if (event.actionType === "add") {
      let rowId = event.data?.row?.id;
      if (this.isRowEmpty(event?.data?.row)) {
        this.toaster.pop({
          text: MessageEnum.EMPTY_ROW,
          type: "error",
        });
      } else {
        this.testCaseData[this.testCaseData.findIndex(tc => tc.id === rowId)].isRowEditable = event.actionType === "edit";
        this.testCaseData = [...this.testCaseData];
        this.refreshPagedTestCaseData();
      }
    }
    if (event.actionType === "edit") {
      let rowId = event.data?.row?.id;
      this.testCaseData[this.testCaseData.findIndex(tc => tc.id === rowId)].isRowEditable = event.actionType === "edit";
      this.testCaseData = [...this.testCaseData];
      this.refreshPagedTestCaseData();
    }
  }

  isRowEmpty(row: any): boolean {
    const ignoreKeys = ['id', 'edit', 'isRowEditable', 'rowSelect', 'expand', 'level'];

    return Object.keys(row)
      .filter(key => !ignoreKeys.includes(key))
      .every(key => {
        const value = row[key];
        return value === '' || value === null || value === undefined;
      });
  }

  goBackToDetailList(): void {
    this.isTSuiteView = true;
    this.selectedTestCaseDetail = null;
    this.showDownloadMenu = false;
    this.showInsertMenu = false;
    this.isEditMode = false;
    this.tcCurrentPage = 0;
    this.testCasePreviewEvent.emit(false);
  }

  toggleDownloadMenu(): void {
    this.showDownloadMenu = !this.showDownloadMenu;
  }

  toggleInsertMenu(): void {
    this.showInsertMenu = !this.showInsertMenu;
  }

  onPersist(): void {
    this.showDownloadMenu = false;
    const payload = {
      id: this.originalTestCaseResponse.id.toString(),
      automationProjectId: this.automationProjectId,
      project_id: this.tdgService.getProjectId()
    };

    this.isLoading = true;
    this.restService.persistTestCaseData(payload).subscribe(
      (response: any) => {
        this.isLoading = false;
        if (response.success) {
          this.toaster.pop({
            text: response.message || MessageEnum.DATA_PERSISTED_SUCCESS,
            type: "success",
          });
        } else {
          this.toaster.pop({
            text: MessageEnum.DATA_PERSISTED_FAILURE,
            type: "error",
          });
        }
      },
      (error) => {
        this.isLoading = false;
        this.toaster.pop({
          text: MessageEnum.DATA_PERSISTED_FAILURE,
          type: "error",
        });
        console.error("Error persisting data:", error);
      }
    );
  }

  onDownloadTestCase(format: string): void {
    this.showDownloadMenu = false;

    // Build the payload - filter out UI-only columns
    const dataColumns = this.testCaseColumns
      .filter(col => col.key !== 'rowSelect' && col.key !== 'edit')
      .map(col => col.key);

    // Transform data to rawData format (array of comma-separated string values)
    const rawData = this.testCaseData.map(row => {
      const values = dataColumns.map(colKey => {
        const value = row[colKey];
        return value !== undefined && value !== null ? `'${value}'` : "''";
      });
      return values.join(", ");
    });

    const payload = {
      tables: [
        {
          columns: dataColumns,
          rawData: rawData,
          tableName: this.originalTestCaseResponse.tableData.testCaseId || this.selectedTestCaseDetail?.testCaseId || 'TestCaseData'
        }
      ]
    };

    this.isLoading = true;
    this.restService.downloadExcelTDG(payload.tables).subscribe(
      (blob: Blob) => {
        this.isLoading = false;

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${payload.tables[0].tableName}.${format}`;
        link.click();
        window.URL.revokeObjectURL(url);

        this.toaster.pop({
          text: `Downloaded as ${format.toUpperCase()} Successfully!`,
          type: "success",
        });
      },
      (error) => {
        this.isLoading = false;
        console.error('Error downloading file:', error);
        this.toaster.pop({
          text: `Failed to Download as ${format.toUpperCase()}`,
          type: "error",
        });
      }
    );
  }

  delete() {
    let type = 'col';
    let items = this.testCaseColumns.filter(col => col.select);

    if (!items.length) {
      items = this.testCaseData.filter((row: any) => row.select);
      type = 'row';
    }

    if (items.length === 0) {
      this.toaster.pop({
        text: MessageEnum.NO_ITEMS_SELECTED_FOR_DELETION,
        type: "warning",
      });
      return;
    }

    if (type === 'col') {
      // Delete selected columns
      items.forEach((col: any) => {
        const colIndex = this.testCaseColumns.findIndex(c => c.key === col.key);
        if (colIndex !== -1) {
          this.testCaseColumns.splice(colIndex, 1);
          // Also remove this column from all rows
          this.testCaseData.forEach((row: any) => {
            delete row[col.key];
          });
        }
      });
      this.testCaseColumns = [...this.testCaseColumns];
    } else {
      // Delete selected rows
      items.forEach((row: any) => {
        const rowIndex = this.testCaseData.findIndex(r => r === row);
        if (rowIndex !== -1) {
          this.testCaseData.splice(rowIndex, 1);
        }
      });
      this.testCaseData = [...this.testCaseData];
    }

    this.refreshPagedTestCaseData();
    this.toaster.pop({
      text: `${items.length} ${type === 'col' ? 'Column(s)' : 'Row(s)'} Deleted Successfully!`,
      type: "success",
    });
  }

  insert(type: 'column' | 'row') {
    if (type === 'row') {
      let obj: any = {
        isRowEditable: true
      };
      this.testCaseColumns.forEach((col: any) => {
        obj[col.key] = '';
      });
      this.testCaseData.push(obj);
      this.testCaseData = [...this.testCaseData];
      this.refreshPagedTestCaseData();
    } else {
      let modalRef = this.modalService.open(AddColumnModalComponent, {
        windowClass:"add-cloumn-window",
        centered: true,
        size: "sm"
      });
      modalRef.result.then((result: any) => {
        if (result) {
          // Insert before the last element (action column)
          this.testCaseColumns.splice(this.testCaseColumns.length - 1, 0, { key: result, display_name: result, _editConfig: { type: "input" }, headerStyles: { 'min-width': '200px !important' }});
          this.testCaseData.forEach((row: any) => {
            row[result] = '';
          });
          this.testCaseData = [...this.testCaseData];
          this.refreshPagedTestCaseData();
        }
      });
    }
    this.toggleInsertMenu();
  }

  saveData() {

    this.testCaseColumns.splice(this.testCaseColumns.findIndex(col => col.key === 'edit'), 1);
    const dataColumns = this.testCaseColumns
      .filter(col => col.key !== 'rowSelect' && col.key !== 'edit')
      .map(col => col.key);

    const rawData = this.testCaseData.map(row => {
      return dataColumns.map(colKey => {
        const value = row[colKey];
        return value !== undefined && value !== null ? value : '';
      });
    });

    const payload = {
      CustomTable: {
        projectId: this.tdgService.getProjectId(),
        tableId: this.originalTestCaseResponse.id,
        testCaseId: this.originalTestCaseResponse.testCaseId,
        testSuite: this.originalTestCaseResponse.testSuite,
        tables: [
          {
            tableName: this.originalTestCaseResponse.tableData.testCaseId || 'CustomTable',
            columns: dataColumns,
            rawData: rawData
          }
        ]
      }
    };

    this.isLoading = true;
    this.restService.updateCustomTableData(payload).subscribe(
      (response: any) => {
        this.isLoading = false;
        this.isEditMode = false;

        // Remove checkbox column after save
        this.testCaseColumns = this.testCaseColumns.filter(col => col.key !== 'rowSelect');

        this.toaster.pop({
          text: MessageEnum.TEST_CASE_DATA_SAVE_SUCCESS,
          type: 'success'
        });

        // Update original response
        this.originalTestCaseResponse.tableData.columns = dataColumns;
        this.originalTestCaseResponse.tableData.rawData = rawData;
      },
      (error) => {
        console.error('Error saving test case data:', error);
        this.isLoading = false;
        this.toaster.pop({
          text: error?.error?.message || MessageEnum.TEST_CASE_DATA_SAVE_FAILURE,
          type: 'error'
        });
      }
    );
  }
}
